/**
 * 学習パスの項目の学び方 (team_curriculums.path_mode)。
 *
 * null は通常、"read" は読むだけ、"optional" は任意。
 * api-go が古くて pathMode が来ない (undefined) ときや、知らない値が来たときは
 * 通常として扱う。画面だけ先に出ても札が出ないだけで、数え方は今までと同じになる。
 *
 * 任意の項目は完了数・完了率の分母から外す。完了していても分子に入れない。
 * 並びの中の位置 (n 本目 / 全体) も同じ分母で数える (path-position.ts の pathItemNumbering)。
 */
export type PathMode = "read" | "optional";

export const PATH_MODE_LABELS: Record<PathMode, string> = {
  read: "読むだけ",
  optional: "任意",
};

/** 管理者の編集画面の選択肢。値の空文字は通常 (NULL に戻す) */
export const PATH_MODE_OPTIONS: ReadonlyArray<{ value: "" | PathMode; label: string }> = [
  { value: "", label: "通常" },
  { value: "read", label: PATH_MODE_LABELS.read },
  { value: "optional", label: PATH_MODE_LABELS.optional },
];

export function normalizePathMode(value: unknown): PathMode | null {
  return value === "read" || value === "optional" ? value : null;
}

/**
 * 進み具合 (完了数・完了率) の分母に入る項目か。**どの画面もこの 1 つの規則で数える。**
 * 区切り (milestone) と任意 (pathMode = "optional") は外す。読むだけと通常は数える。
 * 項目ページとエディターの「項目 n / N」も、この規則で数える (pathItemNumbering)
 */
export function isProgressStep(item: {
  itemType?: string | null;
  pathMode?: string | null;
}): boolean {
  return (
    item.itemType !== "milestone" &&
    normalizePathMode(item.pathMode) !== "optional"
  );
}

/**
 * 管理者の保存の form から学び方を取り出す。欄が無ければ undefined (触らない)。
 * 値の検査は api-go が行うので、ここでは素通しする
 */
export function pathModeFromFormData(formData: FormData): string | undefined {
  if (!formData.has("pathMode")) return undefined;
  const value = formData.get("pathMode");
  return typeof value === "string" ? value : "";
}

/**
 * 完了数と分母を数える。分母は isProgressStep の項目だけで、外した項目は
 * 終えていても分子に入れない。各画面はこれを呼び、完了の判定だけを渡す
 */
export function countPathProgress<
  T extends { itemType?: string | null; pathMode?: string | null },
>(
  items: ReadonlyArray<T>,
  isCompleted: (item: T) => boolean,
): { total: number; completed: number } {
  let total = 0;
  let completed = 0;
  for (const item of items) {
    if (!isProgressStep(item)) continue;
    total += 1;
    if (isCompleted(item)) completed += 1;
  }
  return { total, completed };
}

/**
 * 項目ページで「何本クリアしたか」を数える。バッジの節目 (5 本ごと) に使う。
 * 数える項目は countPathProgress と同じ。保存がまだ届いていない場合に備え、
 * 今開いている項目は完了として数える (分母に入らない項目なら数えない)
 */
export function countClearedSteps(
  lessons: ReadonlyArray<{
    itemType: string;
    displayOrder: number;
    isCompleted?: boolean | null;
    pathMode?: string | null;
  }>,
  currentNum: number,
): number {
  return countPathProgress(
    lessons,
    (lesson) => !!lesson.isCompleted || lesson.displayOrder === currentNum,
  ).completed;
}
